import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  ILogger,
  normalizeUrl,
  useGrippContext,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useCallback } from 'react';
import { Linking, Pressable, StyleSheet } from 'react-native';
import { Card } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { XIcon } from '../crop-circle/icons';
import { colors } from '../crop-circle/tokens';
import { LinkData, MediaComponent, MediaComponentProps } from './types';

export const LinkComponent: MediaComponent<LinkData> = ({
  displayMode = 'card',
  displayWidth = 80,
  onPress,
  canDelete,
  ...props
}: MediaComponentProps<LinkData>) => {
  const { logger } = useGrippContext();
  const linkCardStyle =
    displayMode === 'card' ? styles.linkThumbCard : styles.linkOutlinedCard;
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const displaySize = isCropCircleRedesignEnabled ? 88 : displayWidth;

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress();
      return;
    }
    openLink(props.mediaItem.url, logger);
  }, [props.mediaItem.url, logger, onPress]);

  // TODO: Crop Circle Redesign | Remove conditional rendering when feature is fully launched
  if (!isCropCircleRedesignEnabled) {
    return (
      <Card
        onPress={handlePress}
        mode={displayMode === 'outlined' ? 'outlined' : 'elevated'}
        style={[
          styles.baseCard,
          linkCardStyle,
          { width: displayWidth, height: displayWidth },
        ]}
      >
        <Card.Content style={styles.cardContent}>
          <MaterialCommunityIcons
            name={'link'}
            color={Colors.primaryDark}
            size={displayWidth * 0.4}
          />
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card
      onPress={handlePress}
      mode={displayMode === 'outlined' ? 'outlined' : 'elevated'}
      style={[
        styles.baseCardCropCircle,
        linkCardStyle,
        { width: displaySize, height: displaySize },
      ]}
    >
      {canDelete && (
        <Pressable
          style={styles.closeButton}
          onPress={() => props.deleteMediaItem?.(props.mediaItem.id)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Remove link"
        >
          <XIcon size={8} color={colors.slate[800]} />
        </Pressable>
      )}
      <Card.Content style={styles.cardContent}>
        <MaterialCommunityIcons
          name={'link'}
          color={Colors.primaryDark}
          size={displaySize * 0.4}
        />
      </Card.Content>
    </Card>
  );
};

const openLink = async (url: string, logger?: ILogger) => {
  try {
    const normalizedUrl = normalizeUrl(url);
    await Linking.openURL(normalizedUrl);
  } catch (error) {
    logger?.error(error, `Failed to open link: ${url}`);
  }
};

const styles = StyleSheet.create({
  baseCard: {
    borderRadius: 8,
    backgroundColor: Colors.secondaryGrayHeader,
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
  },
  baseCardCropCircle: {
    borderRadius: 4,
    backgroundColor: Colors.secondaryGrayHeader,
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardContent: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: 0,
    margin: 0,
  },
  linkThumbCard: {},
  linkOutlinedCard: {
    borderWidth: 0.5,
    borderColor: Colors.grayDivider,
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 16,
    height: 16,
    borderRadius: 99,
    backgroundColor: colors.slate[200],
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
});
